import React, { useState, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../components/Common/Sidebar";
import TopBar from "../../components/Common/TopBar";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import AdminOverview from "../../components/AdminDashboard/AdminOverview";
import AdminUsers from "../../components/AdminDashboard/AdminUsers";
import APIAndBilling from "../../components/AdminDashboard/APIAndBilling/Index";
import ContentManagement from "../../components/AdminDashboard/ContentManagment/Index";
import Support from "../../components/AdminDashboard/Support/Index";
import APIUsageLogs from "../../components/AdminDashboard/APIUsageLogs/Index";
import DiscountManagement from "../../components/AdminDashboard/DiscountManagement/Index";
import CreateDiscountCodePage from "../../components/AdminDashboard/DiscountManagement/CreateDiscountCodePage";
import ViewDiscountCodePage from "../../components/AdminDashboard/DiscountManagement/ViewDiscountCodePage";
import ProtectedRoute from "../../components/Common/ProtectedRoute";
import {
  BarChart3,
  Users,
  CreditCard,
  FileText,
  CodeXml,
  Activity,
  Percent
} from "lucide-react";

const AdminDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false); // Unified state for sidebar/drawer
  const [isMobile, setIsMobile] = useState(false);
  const [activeMenu, setActiveMenu] = useState("overview");

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 768;
      setIsMobile(isMobileDevice);
      // On mobile, drawer should be closed by default; on desktop, sidebar should be open
      setIsOpen(!isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update active menu based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/admin-dashboard" || path === "/admin-dashboard/") {
      setActiveMenu("overview");
    } else if (path.includes("/users")) {
      setActiveMenu("users");
    } else if (path.includes("/api-and-billing")) {
      setActiveMenu("api-billing");
    } else if (path.includes("/content-management")) {
      setActiveMenu("content-management");
    } else if (path.includes("/support")) {
      setActiveMenu("support");
    } else if (path.includes("/api-usage-logs")) {
      setActiveMenu("api-usage-logs");
    } else if (path.includes("/discount-management")) {
      setActiveMenu("discount-management");
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const adminMenuItems = [
    {
      id: "overview",
      label: "Dashboard",
      icon: <BarChart3 className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard",
    },
    {
      id: "users",
      label: "Users Management",
      icon: <Users className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/users",
    },
    {
      id: "api-billing",
      label: "API & Billing",
      icon: <CreditCard className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/api-and-billing",
    },
    {
      id: "content-management",
      label: "Content Management",
      icon: <CodeXml className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/content-management",
    },
    {
      id: "support",
      label: "Support",
      icon: <FileText className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/support",
    },
    {
      id: "api-usage-logs",
      label: "API Usage Logs",
      icon: <Activity className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/api-usage-logs",
    },
    {
      id: "discount-management",
      label: "Discount Management",
      icon: <Percent className="h-6 w-6 mr-3" />,
      navigate: "/admin-dashboard/discount-management",
    },
  ];

  // Get current page title based on active menu
  const getCurrentPageTitle = () => {
    if (activeMenu === "overview") {
      return null; // Show welcome message on home page
    }
    const currentMenuItem = adminMenuItems.find(item => item.id === activeMenu);
    return currentMenuItem?.label || null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar/Drawer */}
      {isMobile ? (
        <Drawer
          open={isOpen}
          onClose={closeSidebar}
          direction="left"
          className="bg-white shadow-lg"
          style={{ width: "16rem" }}
        >
          <Sidebar
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            isOpen={true} // Always open inside drawer
            isMobile={isMobile}
            onClose={closeSidebar}
            menuItems={adminMenuItems}
          />
        </Drawer>
      ) : (
        <div
          className={`fixed inset-y-0 left-0 z-30 transition-all duration-300 ease-in-out ${isOpen ? "w-64" : "w-16"
            }`}
        >
          <Sidebar
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            isOpen={isOpen}
            isMobile={isMobile}
            onClose={closeSidebar}
            menuItems={adminMenuItems}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={`
          flex flex-col min-h-screen transition-all duration-300 ease-in-out
          ${isMobile ? "ml-0" : isOpen ? "ml-64" : "ml-16"}
        `}
      >
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="container m-4 bg-white rounded-2xl">
            <TopBar
              userName={user?.firstName + " " + user?.lastName || user?.email || "User"}
              onToggleSidebar={toggleSidebar}
              sidebarOpen={isOpen}
              isMobile={isMobile}
              currentPageTitle={getCurrentPageTitle()}
            />
            <div className="container bg-white rounded-2xl border-b border-gray-200">
            </div>
            <Routes>
              <Route path="/" element={<AdminOverview />} />
              <Route path="/users" element={<AdminUsers />} />
              <Route path="/api-and-billing" element={<APIAndBilling />} />
              <Route path="/content-management" element={<ContentManagement />} />
              <Route path="/support" element={<Support />} />
              <Route path="/api-usage-logs" element={<APIUsageLogs />} />
              <Route path="/discount-management/create" element={<CreateDiscountCodePage />} />
              <Route path="/discount-management/view/:id" element={<ViewDiscountCodePage />} />
              <Route path="/discount-management" element={<DiscountManagement />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export const adminRoutes = [
  {
    path: "/admin-dashboard/*",
    element: (
      <ProtectedRoute allowedRoles={["admin"]}>
        <AdminDashboard />
      </ProtectedRoute>
    ),
  },
];

export default AdminDashboard;